import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Shield, Plus, Edit, Ban, Trash2, CheckCircle, XCircle, KeyRound, Globe2, MapPinned } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';
import { formatAdminScopeSummary, getAdminStatusMeta } from '@/utils/adminGovernance';

const EMPTY_FORM = {
  email: '',
  password: '',
  full_name: '',
  phone: '',
  is_super_admin: false,
  scope_type: 'global',
  scope_ids: [],
  permissions: [],
};

const SCOPE_TYPE_META = {
  global: {
    icon: Globe2,
    helper: 'Acces sur toute la plateforme.',
  },
  country: {
    icon: Globe2,
    helper: 'Acces limite a un ou plusieurs pays.',
  },
  city: {
    icon: MapPinned,
    helper: 'Acces limite a une ou plusieurs villes.',
  },
};

const normalizeCountries = (countries = []) =>
  (countries || [])
    .map((country) => ({
      id: String(country?.code || country?.id || '').trim().toUpperCase(),
      label: String(country?.name || country?.code || '').trim(),
      cities: (country?.cities || [])
        .map((city) => ({
          id: String(city?.code || '').trim().toUpperCase(),
          label: String(city?.name || city?.code || '').trim(),
          countryId: String(country?.code || country?.id || '').trim().toUpperCase(),
          countryLabel: String(country?.name || country?.code || '').trim(),
        }))
        .filter((city) => city.id),
    }))
    .filter((country) => country.id);

const ManageAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [catalog, setCatalog] = useState({ permissions: [], scope_types: [] });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const normalizedCountries = useMemo(() => normalizeCountries(countries), [countries]);
  const cityOptions = useMemo(
    () => normalizedCountries.flatMap((country) => country.cities),
    [normalizedCountries],
  );

  const permissionsByGroup = useMemo(() => {
    const grouped = new Map();
    (catalog.permissions || []).forEach((permission) => {
      const groupKey = String(permission?.group || 'other').trim() || 'other';
      const items = grouped.get(groupKey) || [];
      items.push(permission);
      grouped.set(groupKey, items);
    });
    return Array.from(grouped.entries());
  }, [catalog.permissions]);

  const filteredAdmins = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return admins;
    }
    return admins.filter((admin) => {
      const haystack = [
        admin?.full_name,
        admin?.email,
        admin?.phone,
        admin?.scope_summary,
        ...(admin?.permissions || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [admins, searchQuery]);

  const summary = useMemo(() => {
    const active = admins.filter((admin) => admin?.is_active !== false).length;
    const suspended = admins.length - active;
    const scoped = admins.filter((admin) => !admin?.is_super_admin && admin?.scope_type && admin.scope_type !== 'global').length;
    return { total: admins.length, active, suspended, scoped };
  }, [admins]);

  const loadCurrentUser = async () => {
    const response = await axios.get(`${API}/auth/me`);
    setCurrentUser(response.data);
  };

  const loadAdmins = async () => {
    const response = await axios.get(`${API}/admin/users/by-role/admin`);
    setAdmins(response.data || []);
  };

  const loadCatalog = async () => {
    const response = await axios.get(`${API}/admin/permissions/catalog`);
    setCatalog(response.data || { permissions: [], scope_types: [] });
  };

  const loadCountries = async () => {
    const response = await axios.get(`${API}/countries`);
    setCountries(response.data || []);
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadCurrentUser(), loadAdmins(), loadCatalog(), loadCountries()]);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acces reserve au super admin.');
      } else {
        toast.error('Impossible de charger la gouvernance admin.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const resetForm = () => {
    setEditingAdmin(null);
    setFormData(EMPTY_FORM);
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin?.email || '',
      password: '',
      full_name: admin?.full_name || '',
      phone: admin?.phone || '',
      is_super_admin: Boolean(admin?.is_super_admin),
      scope_type: admin?.scope_type || 'global',
      scope_ids: admin?.scope_ids || [],
      permissions: admin?.permissions || [],
    });
    setShowDialog(true);
  };

  const toggleScopeId = (scopeId) => {
    setFormData((current) => {
      const alreadySelected = (current.scope_ids || []).includes(scopeId);
      return {
        ...current,
        scope_ids: alreadySelected
          ? current.scope_ids.filter((item) => item !== scopeId)
          : [...(current.scope_ids || []), scopeId],
      };
    });
  };

  const togglePermission = (permissionId) => {
    setFormData((current) => {
      const selectedPermissions = current.permissions || [];
      const alreadySelected = selectedPermissions.includes(permissionId);
      return {
        ...current,
        permissions: alreadySelected
          ? selectedPermissions.filter((item) => item !== permissionId)
          : [...selectedPermissions, permissionId],
      };
    });
  };

  const updateScopeType = (scopeType) => {
    setFormData((current) => ({
      ...current,
      scope_type: scopeType,
      scope_ids: scopeType === 'global' ? [] : [],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      email: formData.email,
      full_name: formData.full_name,
      phone: formData.phone,
      permissions: formData.is_super_admin ? [] : formData.permissions,
      scope_type: formData.is_super_admin ? 'global' : formData.scope_type,
      scope_ids: formData.is_super_admin ? [] : formData.scope_ids,
    };

    try {
      if (editingAdmin) {
        await axios.patch(`${API}/admin/users/${editingAdmin.id}`, payload);
        toast.success('Administrateur mis a jour.');
      } else {
        await axios.post(`${API}/admin/users/create`, {
          ...payload,
          password: formData.password,
          is_super_admin: formData.is_super_admin,
          role: 'admin',
        });
        toast.success('Administrateur cree avec succes.');
      }
      setShowDialog(false);
      resetForm();
      await loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l’enregistrement.');
    }
  };

  const handleToggleActive = async (adminRow) => {
    try {
      await axios.patch(`${API}/admin/users/${adminRow.id}/toggle-active?is_active=${!adminRow.is_active}`);
      toast.success(adminRow.is_active ? 'Administrateur suspendu.' : 'Administrateur reactive.');
      await loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de modifier le statut.');
    }
  };

  const handleDelete = async (adminRow) => {
    if (!window.confirm(`Supprimer le compte ${adminRow.full_name} ?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${adminRow.id}`);
      toast.success('Administrateur supprime.');
      await loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de supprimer cet administrateur.');
    }
  };

  const renderScopeOptions = () => {
    if (formData.is_super_admin || formData.scope_type === 'global') {
      return (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          Portee globale. Aucun filtre geographique supplementaire.
        </div>
      );
    }

    const options = formData.scope_type === 'country' ? normalizedCountries : cityOptions;
    if (!options.length) {
      return (
        <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Aucun catalogue geographique disponible pour le moment.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {options.map((option) => {
          const isChecked = (formData.scope_ids || []).includes(option.id);
          const description = formData.scope_type === 'city'
            ? `${option.countryLabel} · ${option.label}`
            : option.label;
          return (
            <label
              key={option.id}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                isChecked ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white hover:border-red-100'
              }`}
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => toggleScopeId(option.id)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{option.id}</span>
                <span className="block text-xs text-gray-500">{description}</span>
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner" /></div>;
  }

  if (!currentUser?.is_super_admin) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Shield className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <p className="text-gray-600">Acces reserve au super admin.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Shield className="h-6 w-6 text-purple-600" />
                Gouvernance des administrateurs
              </CardTitle>
              <CardDescription>
                Creez les admins, definissez leurs permissions modulaires et delimitez leur scope pays ou ville.
              </CardDescription>
            </div>
            <Button
              data-testid="create-admin-btn"
              className="bg-purple-500 hover:bg-purple-600"
              onClick={handleCreate}
            >
              <Plus className="mr-2 h-4 w-4" />
              Nouvel admin
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardDescription>Admins total</CardDescription>
                <CardTitle>{summary.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-green-100 bg-green-50">
              <CardHeader className="pb-2">
                <CardDescription>Actifs</CardDescription>
                <CardTitle className="text-green-700">{summary.active}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-red-100 bg-red-50">
              <CardHeader className="pb-2">
                <CardDescription>Suspendus</CardDescription>
                <CardTitle className="text-red-700">{summary.suspended}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-cyan-100 bg-cyan-50">
              <CardHeader className="pb-2">
                <CardDescription>Scopes limites</CardDescription>
                <CardTitle className="text-cyan-700">{summary.scoped}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {admins.length > 0 && (
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Rechercher un admin par nom, email, scope ou permission..."
              className="max-w-xl"
            />
          )}

          {filteredAdmins.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 px-6 py-12 text-center text-gray-500">
              Aucun administrateur correspondant.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Administrateur</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAdmins.map((adminRow) => {
                  const statusMeta = getAdminStatusMeta(adminRow);
                  return (
                    <TableRow key={adminRow.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{adminRow.full_name}</div>
                          <div className="text-sm text-gray-500">{adminRow.email}</div>
                          {adminRow.phone && <div className="text-xs text-gray-500">{adminRow.phone}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {adminRow.is_super_admin ? (
                          <Badge className="bg-purple-100 text-purple-700">Super admin</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700">Admin</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-900">{adminRow.scope_label || 'Global'}</div>
                          <div className="text-xs text-gray-500">{formatAdminScopeSummary(adminRow)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(adminRow.permissions || []).length === 0 ? (
                            <Badge variant="outline">Permissions heritagees</Badge>
                          ) : (
                            (adminRow.permissions || []).slice(0, 3).map((permission) => (
                              <Badge key={permission} variant="outline">{permission}</Badge>
                            ))
                          )}
                          {(adminRow.permissions || []).length > 3 && (
                            <Badge variant="outline">+{adminRow.permissions.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {!adminRow.is_super_admin && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-admin-${adminRow.id}`}
                              onClick={() => handleEdit(adminRow)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`toggle-admin-${adminRow.id}`}
                              onClick={() => handleToggleActive(adminRow)}
                            >
                              {adminRow.is_active ? (
                                <Ban className="h-4 w-4 text-orange-600" />
                              ) : (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`delete-admin-${adminRow.id}`}
                              onClick={() => handleDelete(adminRow)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{editingAdmin ? 'Modifier l’administrateur' : 'Nouvel administrateur'}</DialogTitle>
            <DialogDescription>
              Structurez ses acces en une seule fois: identite, scope geographique et permissions modulaires.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-full-name">Nom complet *</Label>
                <Input
                  id="admin-full-name"
                  data-testid="admin-full-name"
                  value={formData.full_name}
                  onChange={(event) => setFormData((current) => ({ ...current, full_name: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email *</Label>
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email"
                  value={formData.email}
                  onChange={(event) => setFormData((current) => ({ ...current, email: event.target.value }))}
                  required
                />
              </div>
              {!editingAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Mot de passe *</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    data-testid="admin-password"
                    value={formData.password}
                    onChange={(event) => setFormData((current) => ({ ...current, password: event.target.value }))}
                    required
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="admin-phone">Telephone *</Label>
                <Input
                  id="admin-phone"
                  data-testid="admin-phone"
                  value={formData.phone}
                  onChange={(event) => setFormData((current) => ({ ...current, phone: event.target.value }))}
                  required
                />
              </div>
            </div>

            {!editingAdmin && (
              <label className="flex items-center gap-3 rounded-lg border border-purple-100 bg-purple-50 p-4">
                <Checkbox
                  id="admin-is-super"
                  data-testid="admin-is-super"
                  checked={formData.is_super_admin}
                  onCheckedChange={(checked) => setFormData((current) => ({
                    ...current,
                    is_super_admin: Boolean(checked),
                    permissions: Boolean(checked) ? [] : current.permissions,
                    scope_type: Boolean(checked) ? 'global' : current.scope_type,
                    scope_ids: Boolean(checked) ? [] : current.scope_ids,
                  }))}
                />
                <span className="space-y-1">
                  <span className="block font-medium text-purple-900">Super admin</span>
                  <span className="block text-sm text-purple-700">
                    Acces global, gouvernance des autres admins et contournement des scopes.
                  </span>
                </span>
              </label>
            )}

            <div className="grid gap-6 lg:grid-cols-[1fr,1.2fr]">
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="text-base">Scope administratif</CardTitle>
                  <CardDescription>Definit la zone geographique couverte par cet admin.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-scope-type">Type de scope</Label>
                    <select
                      id="admin-scope-type"
                      data-testid="admin-scope-type"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      disabled={formData.is_super_admin}
                      value={formData.is_super_admin ? 'global' : formData.scope_type}
                      onChange={(event) => updateScopeType(event.target.value)}
                    >
                      {(catalog.scope_types || []).map((scopeType) => (
                        <option key={scopeType.id} value={scopeType.id}>{scopeType.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                    {(() => {
                      const scopeMeta = SCOPE_TYPE_META[formData.is_super_admin ? 'global' : formData.scope_type] || SCOPE_TYPE_META.global;
                      const ScopeIcon = scopeMeta.icon;
                      return (
                        <div className="flex items-start gap-3">
                          <ScopeIcon className="mt-0.5 h-4 w-4 text-gray-500" />
                          <span>{scopeMeta.helper}</span>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="space-y-3">
                    <Label>Zones autorisees</Label>
                    {renderScopeOptions()}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <KeyRound className="h-4 w-4 text-indigo-600" />
                    Permissions modulaires
                  </CardTitle>
                  <CardDescription>
                    Le backend reste la source de verite. Le frontend se cale sur ce catalogue.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.is_super_admin ? (
                    <div className="rounded-lg border border-dashed border-purple-200 bg-purple-50 p-4 text-sm text-purple-800">
                      Le super admin dispose deja de tous les droits.
                    </div>
                  ) : (
                    permissionsByGroup.map(([groupName, items]) => (
                      <div key={groupName} className="space-y-3">
                        <div className="text-sm font-semibold capitalize text-gray-900">{groupName}</div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {items.map((permission) => {
                            const isChecked = (formData.permissions || []).includes(permission.id);
                            return (
                              <label
                                key={permission.id}
                                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                                  isChecked ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'
                                }`}
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={() => togglePermission(permission.id)}
                                />
                                <span className="space-y-1">
                                  <span className="block text-sm font-medium text-gray-900">{permission.label}</span>
                                  <span className="block text-xs text-gray-500">{permission.description}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-admin" className="bg-purple-500 hover:bg-purple-600">
                {editingAdmin ? 'Enregistrer' : 'Creer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAdmins;
